import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, User, ArrowRight, Tag, Clock } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { supabase } from '../utils/supabaseClient';
import { trackEvent } from '../utils/posthog';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  author_id: string;
  category: string;
  tags: string[];
  image_url: string | null;
  status: string;
  published_at: string | null;
  created_at: string;
  author_name?: string;
}

// Categories
const categories = [
  'All Categories',
  'Admissions',
  'Career Guidance',
  'College Rankings',
  'Education Trends',
  'Financial Aid',
  'Student Life',
  'Technology'
];

const Blog: React.FC = () => {
  useScrollToTop();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page: 'blog'
    });
    
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch published blog posts
        const { data: posts, error: postsError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .order('published_at', { ascending: false });
          
        if (postsError) throw postsError;
        
        // Fetch author information for each post
        const postsWithAuthors = await Promise.all(
          (posts || []).map(async (post) => {
            if (post.author_id) {
              try {
                // Try to get user info from public users table
                const { data: userData, error: userError } = await supabase
                  .from('users')
                  .select('name')
                  .eq('id', post.author_id)
                  .maybeSingle();
                  
                if (!userError && userData) {
                  return {
                    ...post,
                    author_name: userData.name
                  };
                }
              } catch (error) {
                console.error('Error fetching author data:', error);
              }
            }
            
            // Default if no author found
            return {
              ...post,
              author_name: 'Editorial Team'
            };
          })
        );
        
        // Set all posts
        setBlogPosts(postsWithAuthors);
        
        // Set featured posts (first 2 posts)
        setFeaturedPosts(postsWithAuthors.slice(0, 2));
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPosts();
  }, []);

  // Filter posts based on search query and selected category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All Categories' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Blog Header */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">11th Admissions Blog</h1>
            <p className="text-lg text-gray-600 mb-8">
              Insights, tips, and guidance for your college admission journey
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <div className="relative flex-grow max-w-xl">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out"
                />
              </div>
              
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out"
              >
                {categories.map((category, index) => (
                  <option key={index} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B4AE2]"></div>
            </div>
          ) : error ? (
            <div className="max-w-4xl mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          ) : (
            <>
              {/* Featured Posts */}
              {featuredPosts.length > 0 && searchQuery === '' && selectedCategory === 'All Categories' && (
                <div className="mb-16">
                  <h2 className="text-2xl font-bold mb-6">Featured Articles</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    {featuredPosts.map(post => (
                      <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={post.image_url || 'https://images.pexels.com/photos/256541/pexels-photo-256541.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'} 
                            alt={post.title} 
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex items-center text-sm text-gray-500 mb-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F0E9FF] text-[#7B4AE2]">
                              <Tag className="h-3 w-3 mr-1" />
                              {post.category}
                            </span>
                            <span className="mx-2">•</span>
                            <span className="flex items-center">
                              <Clock className="h-3.5 w-3.5 mr-1" />
                              {Math.ceil(post.content?.length / 1000)} min read
                            </span>
                          </div>
                          <h3 className="text-xl font-bold mb-2">
                            <Link to={`/blog/${post.slug}`} className="text-gray-900 hover:text-[#7B4AE2]">
                              {post.title}
                            </Link>
                          </h3>
                          <p className="text-gray-600 mb-4">{post.excerpt}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm">
                              <div className="h-8 w-8 rounded-full bg-[#F0E9FF] flex items-center justify-center text-[#7B4AE2] font-medium">
                                {post.author_name?.split(' ').map(n => n[0]).join('')}
                              </div>
                              <span className="ml-2 text-gray-700">{post.author_name}</span>
                            </div>
                            <span className="text-sm text-gray-500 flex items-center">
                              <Calendar className="h-3.5 w-3.5 mr-1" />
                              {new Date(post.published_at || post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* All Posts */}
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {searchQuery || selectedCategory !== 'All Categories' 
                    ? 'Search Results' 
                    : 'Latest Articles'}
                </h2>
                
                {filteredPosts.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
                    <p className="text-gray-600">
                      Try adjusting your search or filter criteria.
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPosts.map(post => (
                      <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
                        <div className="h-40 overflow-hidden">
                          <img 
                            src={post.image_url || 'https://images.pexels.com/photos/256541/pexels-photo-256541.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'} 
                            alt={post.title} 
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                        </div>
                        <div className="p-5 flex-grow flex flex-col">
                          <div className="flex items-center text-xs text-gray-500 mb-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#F0E9FF] text-[#7B4AE2]">
                              {post.category}
                            </span>
                            <span className="mx-2">•</span>
                            <span className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {Math.ceil(post.content?.length / 1000)} min read
                            </span>
                          </div>
                          <h3 className="text-lg font-bold mb-2 line-clamp-2">
                            <Link to={`/blog/${post.slug}`} className="text-gray-900 hover:text-[#7B4AE2]">
                              {post.title}
                            </Link>
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-grow">{post.excerpt}</p>
                          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                            <span className="text-xs text-gray-500 flex items-center">
                              <User className="h-3 w-3 mr-1" />
                              {post.author_name}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {new Date(post.published_at || post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Newsletter Signup */}
              <div className="mt-16 bg-[#F0E9FF] rounded-lg p-8 max-w-4xl mx-auto">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-2">Subscribe to Our Newsletter</h2>
                  <p className="text-gray-600">
                    Get the latest articles, tips, and insights delivered to your inbox
                  </p>
                </div>
                <form className="flex flex-col md:flex-row gap-4 max-w-xl mx-auto">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="flex-grow px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B4AE2] focus:border-[#7B4AE2]"
                    required
                  />
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#7B4AE2] text-white rounded-md hover:bg-[#6A3ACF] transition-colors"
                  >
                    Subscribe
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Blog;